import type { ReviewMetric, RideTrackPoint } from '@motogo/shared';
import { createContext, useCallback, useContext, useMemo, useState, type FC, type ReactNode } from 'react';

/**
 * Bridges the ride-detail screen and the persistent map. The detail page (rendered in the
 * router Outlet) sets the track under review and the scrub-cursor index; `MapView` (the
 * persistent shell) reacts — drawing the speed-gradient track, fitting bounds, and moving
 * the cursor marker. Same data-down pattern as peer locations: state here, the map reads it.
 * See CLAUDE.md → Ride Review &amp; Display.
 */
interface RideReviewValue {
  /** Track currently under review (drawn on the map), or null when on the map screen. */
  track: RideTrackPoint[] | null;
  /** Index into `track` of the scrub cursor, or null for no cursor. */
  scrubIndex: number | null;
  /** Metric the map line is coloured by. */
  metric: ReviewMetric;
  /** Set (or clear, with null) the track under review. Clearing also resets the cursor + metric. */
  setReview: (track: RideTrackPoint[] | null) => void;
  setScrubIndex: (index: number | null) => void;
  setMetric: (metric: ReviewMetric) => void;
}

const RideReviewContext = createContext<RideReviewValue | null>(null);

export const RideReviewProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [track, setTrack] = useState<RideTrackPoint[] | null>(null);
  const [scrubIndex, setScrubIndex] = useState<number | null>(null);
  const [metric, setMetric] = useState<ReviewMetric>('speed');

  // Stable identities (useCallback / the raw setState setter) so consumers can depend on
  // them in effects without re-firing whenever the track or cursor changes.
  const setReview = useCallback((next: RideTrackPoint[] | null) => {
    setTrack(next);
    setScrubIndex(null);
    setMetric('speed'); // each ride opens coloured by speed
  }, []);

  const value = useMemo<RideReviewValue>(
    () => ({ track, scrubIndex, metric, setReview, setScrubIndex, setMetric }),
    [track, scrubIndex, metric, setReview],
  );

  return <RideReviewContext.Provider value={value}>{children}</RideReviewContext.Provider>;
};

export function useRideReview(): RideReviewValue {
  const ctx = useContext(RideReviewContext);
  if (!ctx) {
    throw new Error('useRideReview must be used within a RideReviewProvider');
  }
  return ctx;
}
